import { formatPercent, TurnoverResult, TurnoverValues } from "models/hr-dashboard";
import MonthHeader from "./month-header";

interface TurnoverTableProps {
    data: TurnoverResult;
}

const COUNT_ROWS: { label: string; key: keyof TurnoverValues }[] = [
    { label: "Non-Regrettable", key: "nonRegrettable" },
    { label: "Regrettable", key: "regrettable" },
    { label: "Unclassified", key: "unclassified" },
];

const quarterColor = (over: boolean | null) =>
    over === null ? undefined : over ? "var(--bs-danger-bg-subtle)" : "var(--bs-success-bg-subtle)";

const TurnoverTable = ({ data }: TurnoverTableProps) => {
    const showUnclassified = (data.ytd?.unclassified ?? 0) > 0;
    const countRows = COUNT_ROWS.filter(r => r.key !== "unclassified" || showUnclassified);

    return (
        <>
            <div className="table-responsive">
                <table className="table table-sm table-bordered mb-3">
                    <MonthHeader months={data.months} fiscalYear={data.fiscalYear} label="Employees" />
                    <tbody>
                        <tr className="fw-bold">
                            <td>Month Turnover</td>
                            {data.months.map(m => (
                                <td key={m.key} className="text-end">{m.values ? formatPercent(m.values.rate) : "—"}</td>
                            ))}
                            <td className="text-end">{formatPercent(data.ytd?.rate)}</td>
                        </tr>
                        {countRows.map(row => (
                            <tr key={row.key}>
                                <td>{row.label}</td>
                                {data.months.map(m => (
                                    <td key={m.key} className="text-end">{m.values ? m.values[row.key] : "—"}</td>
                                ))}
                                <td className="text-end fw-bold">{data.ytd ? data.ytd[row.key] : ""}</td>
                            </tr>
                        ))}
                        <tr className="fw-bold">
                            <td>Total Left</td>
                            {data.months.map(m => (
                                <td key={m.key} className="text-end">{m.values ? m.values.left : "—"}</td>
                            ))}
                            <td className="text-end">{data.ytd?.left ?? ""}</td>
                        </tr>
                        <tr className="fw-bold">
                            <td>Quarter Turnover</td>
                            {data.quarters.map(q => (
                                <td key={q.quarter} colSpan={3} className="text-center"
                                    style={{ backgroundColor: quarterColor(q.overThreshold) }}>
                                    {q.values ? formatPercent(q.values.rate) : "—"}
                                </td>
                            ))}
                            <td />
                        </tr>
                        <tr>
                            <td>Maximum Level</td>
                            {data.quarters.map(q => (
                                <td key={q.quarter} colSpan={3} className="text-center">{formatPercent(data.quarterMax)}</td>
                            ))}
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>

            <h5 className="mb-2">Year to date</h5>
            <div className="table-responsive" style={{ maxWidth: 480 }}>
                <table className="table table-sm table-bordered mb-0">
                    <thead>
                        <tr>
                            <th></th>
                            <th className="text-end">Count</th>
                            <th className="text-end">Turnover</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Non-Regrettable</td>
                            <td className="text-end">{data.ytd?.nonRegrettable ?? "—"}</td>
                            <td className="text-end">{formatPercent(data.ytd?.nonRegrettableRate)}</td>
                        </tr>
                        <tr>
                            <td>Regrettable</td>
                            <td className="text-end">{data.ytd?.regrettable ?? "—"}</td>
                            <td className="text-end">{formatPercent(data.ytd?.regrettableRate)}</td>
                        </tr>
                        {showUnclassified && (
                            <tr>
                                <td>Unclassified</td>
                                <td className="text-end">{data.ytd?.unclassified}</td>
                                <td className="text-end">{formatPercent(data.ytd?.unclassifiedRate)}</td>
                            </tr>
                        )}
                        <tr className="fw-bold">
                            <td>Total</td>
                            <td className="text-end">{data.ytd?.left ?? "—"}</td>
                            <td className="text-end">{formatPercent(data.ytd?.rate)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default TurnoverTable;
